import { motion } from 'framer-motion';
import {
  TagIcon,
  AddIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@/config/icons';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { SortableTagItem } from '../SortableTagItem';
import { TicketTag } from '../SidebarDialogs';

interface TagsSectionProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  loading: boolean;
  tags: TicketTag[];
  currentTagId: string | null;
  onTagClick: (tagId: string) => void;
  onNewTag: () => void;
  onEditTag: (tag: TicketTag) => void;
  onDeleteTag: (tag: TicketTag) => void;
  onDragEnd: (event: DragEndEvent) => void;
}

export function TagsSection({
  open,
  onOpenChange,
  loading,
  tags,
  currentTagId,
  onTagClick,
  onNewTag,
  onEditTag,
  onDeleteTag,
  onDragEnd,
}: TagsSectionProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  return (
    <Collapsible open={open} onOpenChange={onOpenChange}>
      <div className="flex items-center justify-between">
        <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
          <TagIcon className="h-4 w-4" />
          <span>TAGS</span>
          {open ? (
            <ChevronUpIcon className="h-3 w-3" />
          ) : (
            <ChevronDownIcon className="h-3 w-3" />
          )}
        </CollapsibleTrigger>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 rounded-full bg-muted hover:bg-muted/80"
          onClick={onNewTag}
        >
          <AddIcon className="h-3 w-3" />
        </Button>
      </div>

      <CollapsibleContent className="mt-2 space-y-0.5">
        {loading ? (
          <motion.div
            className="space-y-1 px-1"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {[...Array(3)].map((_, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05, duration: 0.2 }}
              >
                <Skeleton className="h-7 w-full rounded-lg" />
              </motion.div>
            ))}
          </motion.div>
        ) : tags.length === 0 ? (
          <p className="text-xs text-muted-foreground px-3 py-2">Aucun tag</p>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onDragEnd}
          >
            <SortableContext
              items={tags.map(t => t.id)}
              strategy={verticalListSortingStrategy}
            >
              {tags.map((tag) => (
                <SortableTagItem
                  key={tag.id}
                  id={tag.id}
                  name={tag.name}
                  color={tag.color}
                  ticketCount={tag.ticket_count}
                  isActive={currentTagId === tag.id}
                  onClick={() => onTagClick(tag.id)}
                  onEdit={() => onEditTag(tag)}
                  onDelete={() => onDeleteTag(tag)}
                />
              ))}
            </SortableContext>
          </DndContext>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}
